import React, { useMemo, useState } from "react";
import "./NurtureCampaignContactSearch.css";
import SearchOrAddDropdown from "./SearchOrAddDropdown";

/**
 * NurtureCampaignContactSearch
 *
 * Props:
 * - initialTitle?: string | string[]   // comma-separated or array
 * - initialSeniority?: string | string[]
 * - initialLocation?: string | string[]
 * - seniorityOptions?: string[]
 * - locationOptions?: string[]
 * - onSearch?: (form: { titles: string[], person_seniorities: string[], person_locations: string[] }) => void
 */
export default function NurtureCampaignContactSearch({
  initialTitle = "",
  initialSeniority = [],
  initialLocation = [],
  seniorityOptions,
  locationOptions,
  onSearch = () => {},
}) {
  const defaultSeniorities = useMemo(
    () => [
      "owner",
      "founder",
      "c_suite",
      "partner",
      "vp",
      "head",
      "director",
      "manager",
      "senior",
      "entry",
      "intern",
    ],
    []
  );

  const defaultLocations = useMemo(
    () => ["United States", "India", "Germany", "United Kingdom", "Canada"],
    []
  );

  const seniors = seniorityOptions?.length ? seniorityOptions : defaultSeniorities;
  const locations = locationOptions?.length ? locationOptions : defaultLocations;

  const toArr = (v) =>
    Array.isArray(v) ? v.filter(Boolean) : v ? [String(v)] : [];

  const [form, setForm] = useState({
    titleInput: Array.isArray(initialTitle) ? initialTitle.join(", ") : initialTitle,
    seniority: toArr(initialSeniority),
    location: toArr(initialLocation),
  });

  const handleTitleChange = (e) => {
    setForm((prev) => ({ ...prev, titleInput: e.target.value }));
  };

  const onChange = (key) => (next) =>
    setForm((prev) => ({
      ...prev,
      [key]: Array.isArray(next) ? next : toArr(next),
    }));

  const isDisabled =
    form.titleInput.trim() === "" &&
    (!form.seniority || form.seniority.length === 0) &&
    (!form.location || form.location.length === 0);

  const handleSubmit = () => {
    const titles = form.titleInput
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean);

    onSearch({
      titles,
      person_seniorities: form.seniority,
      person_locations: form.location,
    });
  };

  const handleReset = () => {
    // Clear UI state
    setForm({
      titleInput: "",
      seniority: [],
      location: [],
    });

    // Fire reset search
    onSearch({
      titles: [],
      person_seniorities: [],
      person_locations: [],
    });
  };

  const mapToOption = (arr) => arr.map((val) => ({ label: val, value: val }));

  return (
    <form
      className="nccs-wrap"
      onSubmit={(e) => {
        e.preventDefault();
        handleSubmit();
      }}
      aria-label="Nurture Campaign Contact Search"
    >
      <div className="nccs-grid">
        {/* Title input (comma-separated) */}
        <label className="nccs-field">
          <span className="nccs-label stf-label">Current Job Title</span>
          <input
            className="nccs-input stf-input"
            type="text"
            placeholder="e.g., Strategy, Product Manager"
            value={form.titleInput}
            onChange={handleTitleChange}
            autoComplete="off"
          />
        </label>

        {/* Seniority dropdown */}
        <label className="nccs-field">
          <span className="nccs-label stf-label">Seniority</span>
          <SearchOrAddDropdown
            placeholder="Select Seniority"
            options={mapToOption([...new Set([...seniors, ...form.seniority])])}
            value={form.seniority}
            onChange={onChange("seniority")}
          />
        </label>

        {/* Location dropdown */}
        <label className="nccs-field nccs-col-span-1">
          <span className="nccs-label stf-label">Locations</span>
          <SearchOrAddDropdown
            placeholder="Select Locations"
            options={mapToOption([...new Set([...locations, ...form.location])])}
            value={form.location}
            onChange={onChange("location")}
          />
        </label>

        {/* Actions */}
        <div className="nccs-actions">
          <button
            type="submit"
            className="nccs-btn"
            disabled={isDisabled}
            title={isDisabled ? "Add at least one filter" : "Search"}
          >
            Search
          </button>

          {/* IMPORTANT: not submit; it should clear and fire empty onSearch */}
          <button
            type="button"
            className="nccs-btn nccs-btn--ghost"
            title="Reset Search"
            onClick={handleReset}
          >
            Reset
          </button>
        </div>
      </div>
    </form>
  );
}
